/**
 * The analytics entry point. Everything the app emits goes through `track()`.
 *
 * The shape is deliberate: one function that events flow into, and a registry of
 * destinations that decides where each one goes. Call sites never name a sink, so
 * "send page views to GA and our backend, send scroll depth only to our backend"
 * is a change to `accepts` in one file rather than a change everywhere.
 *
 * See ./destinations for the registry, ./pageView for the payload, and
 * ./session for how a guest is identified.
 */

import { buildBrowseFilterAppliedEvent } from "./browseFilterApplied";
import { DESTINATIONS } from "./destinations";
import { buildInvitationClickedEvent } from "./invitationClicked";
import { buildInvitationOutcomeEvent, errorCodeForStatus } from "./invitationOutcome";
import { buildInvitationsDropdownOpenedEvent } from "./invitationsDropdownOpened";
import { buildNotificationClickedEvent } from "./notificationClicked";
import { buildNotificationsDropdownOpenedEvent } from "./notificationsDropdownOpened";
import { buildPageViewEvent } from "./pageView";
import { onSessionMint } from "./session";
import { buildSessionStartEvent } from "./sessionStart";
import { buildTopicAnalysisPersonaReviewOpenedEvent } from "./topicAnalysisPersonaReviewOpened";
import { buildSearchPerformedEvent } from "./searchPerformed";
import { buildSearchResultClickedEvent } from "./searchResultClicked";
import type { AnalyticsEvent } from "./types";

export { clearSessionId, getSessionId, resetSessionMintListener } from "./session";
export {
  buildBrowseFilterAppliedEvent,
  buildInvitationClickedEvent,
  buildInvitationOutcomeEvent,
  buildInvitationsDropdownOpenedEvent,
  buildNotificationClickedEvent,
  buildNotificationsDropdownOpenedEvent,
  buildSearchPerformedEvent,
  buildSearchResultClickedEvent,
  buildTopicAnalysisPersonaReviewOpenedEvent,
  errorCodeForStatus,
};
export type {
  AnalyticsEvent,
  BrowseFilterAction,
  BrowseFilterAppliedEvent,
  DeviceType,
  InvitationClickedAction,
  InvitationClickedEvent,
  InvitationErrorCode,
  InvitationOutcomeEvent,
  InvitationOutcomeType,
  InvitationsDropdownOpenedEvent,
  NotificationClickedEvent,
  NotificationsDropdownOpenedEvent,
  PageViewEvent,
  PersonaReviewSource,
  SearchPerformedEvent,
  SearchResultClickedEvent,
  SessionStartEvent,
  SessionStartProperties,
  TopicAnalysisPersonaReviewOpenedEvent,
} from "./types";

/**
 * One-time SDK setup for every enabled destination. Call once, at startup,
 * before the first `track()`.
 */
export const initAnalytics = (): void => {
  // Registered before the first track() so the very first session — the one that
  // mints its id inside the initial page view — is described rather than silently
  // skipped. `session_start` therefore lands just ahead of the `page_view` that
  // triggered the mint, which is the order the data should read in.
  onSessionMint((id) => track(buildSessionStartEvent(id)));

  for (const destination of DESTINATIONS) {
    if (!destination.isEnabled()) continue;

    try {
      destination.init?.();
    } catch (error) {
      warn(destination.name, "init", error);
    }
  }
};

/**
 * Fans an event out to every enabled destination that accepts it.
 *
 * Each `send` is isolated: a destination that throws does not prevent the ones
 * after it from receiving the event, and no analytics failure ever reaches the
 * user.
 */
export const track = (event: AnalyticsEvent): void => {
  for (const destination of DESTINATIONS) {
    try {
      if (!destination.isEnabled()) continue;
      if (!destination.accepts(event)) continue;
      destination.send(event);
    } catch (error) {
      warn(destination.name, "send", error);
    }
  }
};

interface RouterStateLike {
  location: { pathname: string; search?: string };
  matches?: Array<{ route?: { path?: string }; params?: Record<string, string | undefined> }>;
}

/**
 * Dedupe key for a navigation.
 *
 * `pathname + search`, not pathname alone. The previous GA-only implementation
 * compared pathnames, which meant a navigation that changed only the query string
 * — `?tab=members`, a filter, a step in a wizard — reported nothing at all.
 * Keying on both also collapses the cases we genuinely want collapsed: the router
 * notifies on every state transition, including ones that resolve to the same URL.
 */
const locationKey = (state: RouterStateLike): string =>
  `${state.location.pathname}${state.location.search ?? ""}`;

let lastTrackedKey: string | null = null;

/**
 * Records a page view for a router state, unless it is the same location we just
 * recorded.
 *
 * Deduping here rather than per destination is what keeps GA and our own store
 * agreeing on the count. Two subscribers with two different dedupe rules is how
 * numbers start disagreeing and everyone stops trusting them.
 *
 * This also absorbs redirect chains — `/hubs/7` bouncing through
 * `ProtectedOutlet` to `/auth/login` settles on one final location — and the
 * double-fire between the initial load and the router's first notification.
 */
export const trackPageView = (state: RouterStateLike): void => {
  const key = locationKey(state);
  if (key === lastTrackedKey) return;
  lastTrackedKey = key;

  track(buildPageViewEvent(state));
};

/** Test seam: forget the last tracked location. */
export const resetPageViewDedupe = (): void => {
  lastTrackedKey = null;
};

const warn = (name: string, phase: string, error: unknown): void => {
  // Analytics problems are diagnostics, never user-facing. Console only — routing
  // them to Sentry would make a blocked tracker look like an application error.
  console.warn(`[analytics] destination "${name}" failed during ${phase}`, error);
};
